import React, { useMemo, useState } from 'react';
import { ActionButton, SearchBar, MultiSelectList, FilterPanel } from '@/shared/components/ui';
import { DEPARTMENT_OPTIONS, POSITION_OPTIONS, employeeRecords } from '@/shared/data';
import { useUIStore } from '@/stores/uiStore';

// LocalStorage key for assignments: scheduleId -> array of employee ids
const ASSIGN_KEY = 'csas.workSchedule.assignments';

function loadAssignments() {
  try {
    const raw = localStorage.getItem(ASSIGN_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveAssignments(map) {
  try { localStorage.setItem(ASSIGN_KEY, JSON.stringify(map)); } catch {}
}

export default function AssignWorkScheduleModal({ onClose, data }) {
  const schedule = data?.schedule;
  const { showSuccessToast } = useUIStore();
  const [dept, setDept] = useState('');
  const [pos, setPos] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => {
    const map = loadAssignments();
    return new Set(map[schedule?.id] || []);
  });

  const departmentOptions = useMemo(() => [{ key: '', label: 'All Departments' }, ...DEPARTMENT_OPTIONS.map(d => ({ key: d.key, label: d.label }))], []);
  const positionOptions = useMemo(() => [{ key: '', label: 'All Positions' }, ...POSITION_OPTIONS.map(p => ({ key: p.key, label: p.label }))], []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return employeeRecords.filter(emp => {
      if (dept && emp.department !== dept) return false;
      if (pos && emp.position !== pos) return false;
      if (q && !(emp.fullName || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [dept, pos, query]);

  const toggle = (id) => {
    setSelected(s => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length && filtered.every(e => selected.has(e.id));
  const toggleSelectAll = () => {
    setSelected(s => {
      const next = new Set(s);
      if (allVisibleSelected) {
        filtered.forEach(f => next.delete(f.id));
      } else {
        filtered.forEach(f => next.add(f.id));
      }
      return next;
    });
  };

  const assign = () => {
    if (!schedule) return;
    const map = loadAssignments();
    map[schedule.id] = Array.from(selected);
    saveAssignments(map);
    showSuccessToast('Work schedule assigned', `${selected.size} employees assigned to ${schedule.name}.`);
    onClose();
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Assign Work Schedule</h2>
        <p className="text-sm text-neutral-500">Select employees to assign <span className="font-medium">{schedule?.name}</span>.</p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <FilterPanel
          site={dept}
          onSiteChange={setDept}
          siteOptions={departmentOptions}
          icon={null}
          autoWidth
          className="md:w-auto"
        >
          {/* position filter nested to align visually */}
        </FilterPanel>
        <FilterPanel
          site={pos}
          onSiteChange={setPos}
          siteOptions={positionOptions}
          icon={null}
          autoWidth
          className="md:w-auto"
        />
        <div className="flex-1 min-w-[180px]">
          <SearchBar
            value={query}
            placeholder="Search name..."
            onChange={(e) => setQuery(e.target.value)}
            onSubmit={() => { /* submit-only pattern not needed; live filter */ }}
          />
        </div>
      </div>

      <MultiSelectList
        items={filtered}
        selected={selected}
        getId={(e) => e.id}
        renderLabel={(e) => e.fullName}
        allVisibleSelected={!!allVisibleSelected}
        onToggle={toggle}
        onToggleAll={toggleSelectAll}
        headerLabel="Employee Name"
        emptyMessage="No employees match current filters."
        height="18rem"
      />

      <div className="flex justify-end gap-3 pt-2">
        <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
        <ActionButton variant="primary" onClick={assign} disabled={!selected.size}>Assign ({selected.size})</ActionButton>
      </div>
    </div>
  );
}

export { ASSIGN_KEY, loadAssignments, saveAssignments };